import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import Header from './Header';
import Footer from './Footer';
import './FAQPage.css';

const FAQ_CATEGORIES = [
  {
    category: 'Using the Tracking Tool',
    items: [
      {
        question: 'How do I track a shipment on RideLink Tracking?',
        answer: 'Go to the Track Shipment page, enter your container number (e.g., MSKU1234567), Bill of Lading, or booking number in the search field, and click the search button. Results are returned instantly.',
      },
      {
        question: 'What types of reference numbers can I use to search?',
        answer: 'Ride link tracking accepts three types: (1) Container Number  a 4 letter prefix plus 7 digits, e.g., MSKU1234567; (2) Bill of Lading (B/L)  the document number issued by the carrier after loading; (3) Booking Number  the reference you receive when booking cargo space. All three return the same full tracking result.',
      },
      {
        question: 'Do I need an account to track a shipment?',
        answer: 'Basic tracking is available without an account. However, signing in unlocks additional features including tracking history, delay alerts, multi-shipment dashboards, and the ability to save and revisit previous searches.',
      },
      {
        question: 'How do credits work?',
        answer: 'Each detailed tracking lookup consumes one credit from your balance. Credits can be purchased in bundles from your profile page. Free credits are available on sign-up. Basic search previews are free and do not consume credits.',
      },
      {
        question: 'Can I track air cargo as well as ocean containers?',
        answer: 'Yes. RideLink Tracking supports both ocean container tracking and air cargo tracking using Air Waybill (AWB) numbers. Switch between sea and air tracking modes using the selector in the tracking interface.',
      },
      {
        question: 'Why does the tracking result show "Pending" or "No Data"?',
        answer: '"Pending" means the container has been booked but not yet gated into the port terminal. "No Data" typically means the reference number hasn\'t been registered with the carrier yet, or there is a delay in data reporting from the terminal. Try again after a few hours or verify the number with your freight forwarder.',
      },
      {
        question: 'Can I track multiple containers at the same time?',
        answer: 'Yes. After signing in, your dashboard shows all containers you have tracked. You can also use the API to batch query multiple containers simultaneously if you have an API enabled plan.',
      },
      {
        question: 'How do I read the tracking timeline?',
        answer: 'The tracking timeline shows each port event in chronological order: gate in (container arrives at origin port), loading (placed on vessel), departure, arrival at destination or transshipment port, discharge (removed from vessel), and gate-out (container leaves terminal). Each event includes the location, timestamp, and vessel name.',
      },
    ],
  },
  {
    category: 'General Tracking',
    items: [
      {
        question: 'What is container tracking?',
        answer: 'Container tracking is the process of monitoring the real-time location and status of a shipping container as it moves through the global supply chain — from the origin port through transshipment hubs to the final destination port. It uses data from carriers, terminals, and AIS vessel signals to show where your cargo is at any given moment.',
      },
      {
        question: 'What information do I need to track a shipment?',
        answer: 'You need one of three reference numbers: (1) a Container Number (e.g., MSKU1234567), (2) a Bill of Lading (B/L) number issued by the carrier, or (3) a Booking Number provided when the shipment was booked. Any one of these is sufficient to retrieve full tracking results.',
      },
      {
        question: 'How often is tracking data updated?',
        answer: 'Tracking data is updated at each port event  including gate-in, loading, departure, arrival, discharge, and gate-out. At sea, vessel position data via AIS can update every few minutes. In total, most active shipments receive multiple updates per day.',
      },
      {
        question: 'Why is my shipment showing no recent updates?',
        answer: 'If your container is at sea between two ports, there may be no new port event to report. AIS vessel tracking will still show vessel movement. If the container has been at a terminal for more than 72 hours with no update, the terminal may be experiencing a data reporting delay — check back in 24 hours or contact the carrier directly.',
      },
      {
        question: 'Can I track multiple containers at once?',
        answer: 'Yes. After signing in to RideLink Tracking, you can add multiple containers to your dashboard and monitor all of them from a single view. You can also export tracking history in CSV format for record-keeping.',
      },
    ],
  },
  {
    category: 'Container Numbers & Codes',
    items: [
      {
        question: 'What is a container number and how do I read it?',
        answer: 'A container number follows the ISO 6346 format: 4 letters + 6 digits + 1 check digit (e.g., MSKU 123456 7). The first 3 letters are the owner code (identifying the container owner), the 4th letter is always "U" for freight containers, the 6 digits are the serial number, and the final digit is a check digit used to verify the number is correct.',
      },
      {
        question: 'What is a container prefix?',
        answer: 'A container prefix (or owner code) is the first 3 letters of a container number. It identifies who owns the container — either a shipping line (e.g., MSK for Maersk, MSC for Mediterranean Shipping Company) or a leasing company (e.g., TRL for Triton). You can browse the full list on our Container Prefixes page.',
      },
      {
        question: 'What is a SCAC code?',
        answer: 'A SCAC (Standard Carrier Alpha Code) is a 2–4 letter code assigned by the National Motor Freight Traffic Association (NMFTA) to uniquely identify transportation companies. For ocean carriers, SCACs are used in shipping documents, EDI messages, and customs filings. Examples: MAEU (Maersk), MSCU (MSC), HLCU (Hapag-Lloyd).',
      },
      {
        question: 'What is a Bill of Lading (B/L)?',
        answer: 'A Bill of Lading is a legal document issued by a carrier to acknowledge receipt of cargo for shipment. It serves three purposes: (1) a receipt for the goods, (2) a contract of carriage, and (3) a document of title. The B/L number is one of the reference numbers you can use to track a shipment on RideLink Tracking.',
      },
      {
        question: 'What is the difference between a booking number and a Bill of Lading?',
        answer: 'A booking number is assigned when you reserve space on a vessel before the cargo is loaded. A Bill of Lading is issued after the cargo has been received by the carrier or loaded on the vessel. Both can be used for tracking, but the B/L is the more formal shipping document.',
      },
    ],
  },
  {
    category: 'Carriers & Routes',
    items: [
      {
        question: 'Which shipping carriers does RideLink Tracking support?',
        answer: 'RideLink Tracking supports all major ocean carriers including Maersk, MSC, CMA CGM, Hapag-Lloyd, ONE (Ocean Network Express), HMM, Evergreen, COSCO, Yang Ming, ZIM, and more — covering over 95% of global container capacity. We also track containers from all major leasing companies (Triton, Beacon, Textainer, etc.).',
      },
      {
        question: 'Why does a container sometimes switch between carriers?',
        answer: 'This can happen in two scenarios: (1) transshipment — the container is transferred to a different vessel (sometimes operated by a different carrier under an alliance agreement), or (2) leased containers — the same container equipment may be operated by different carriers on successive voyages. Both are tracked seamlessly by RideLink Tracking.',
      },
      {
        question: 'What are shipping alliances?',
        answer: 'Shipping alliances are cooperative agreements between ocean carriers to share vessel space and coordinate schedules. The main alliances are: THE Alliance (Hapag-Lloyd, ONE, HMM, Yang Ming), OCEAN Alliance (CMA CGM, COSCO, Evergreen, OOCL), and Gemini Cooperation (Maersk, Hapag-Lloyd from 2025). Alliance cooperation means a Maersk booking may travel on a Hapag-Lloyd vessel, for example.',
      },
      {
        question: 'What causes shipping delays?',
        answer: 'Common causes include: port congestion (especially at major hubs like Shanghai, Rotterdam, or Los Angeles), adverse weather (typhoons, storms), customs holds, equipment shortages, and vessel schedule changes. RideLink Tracking sends proactive delay alerts so you are notified as soon as a delay is detected, before the carrier\'s own notifications.',
      },
    ],
  },
  {
    category: 'RideLink Tracking Features',
    items: [
      {
        question: 'What tracking features does RideLink Tracking offer that carriers don\'t?',
        answer: 'RideLink Tracking extends basic carrier tracking with: (1) Delay & Early Arrival Alerts via email or webhook, (2) AI-powered ETA Prediction, (3) CO₂ Emission Tracking per shipment, (4) Multi-Carrier Dashboard for all your containers in one view, (5) CSV Export for reporting, and (6) API access for system integrations.',
      },
      {
        question: 'How do delay alerts work?',
        answer: 'When RideLink Tracking detects that a vessel\'s ETA has changed by more than a configurable threshold (default: 12 hours), it sends an automated alert to your registered email address. You can also set up webhook notifications for integration with Slack, Teams, or your own systems.',
      },
      {
        question: 'What is ETA Prediction?',
        answer: 'ETA Prediction uses machine learning to estimate arrival dates based on historical patterns for the same route, current vessel speed, weather forecasts, and port congestion data. It often provides more accurate estimates than the carrier\'s published schedule — especially for time-sensitive cargo.',
      },
      {
        question: 'Does RideLink Tracking offer CO₂ tracking?',
        answer: 'Yes. RideLink Tracking calculates the estimated CO₂ emissions for each container shipment based on vessel fuel consumption data, route distance, and cargo weight. This data can be used for sustainability reporting, carbon offsetting, and regulatory compliance (e.g., EU CBAM or Scope 3 emissions reporting).',
      },
      {
        question: 'Is there an API for container tracking?',
        answer: 'Yes. RideLink Tracking offers a REST API that allows you to integrate container tracking data directly into your TMS, ERP, or custom applications. The API supports tracking by container number, B/L, and booking number and returns structured JSON with port events, vessel details, and ETA predictions.',
      },
    ],
  },
];

function FAQPage() {
  const [openMap, setOpenMap] = useState({});

  const toggle = (catIndex, itemIndex) => {
    const key = `${catIndex}-${itemIndex}`;
    setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isOpen = (catIndex, itemIndex) => !!openMap[`${catIndex}-${itemIndex}`];

  return (
    <div className="faq-page">
      <SEO
        title="FAQ — Shipping & Container Tracking Questions Answered"
        description="Answers to common questions about container tracking, Bill of Lading, SCAC codes, shipping carriers, and RideLink Tracking features."
        canonicalPath="/faq"
      />
      <Header />

      {/* ── HERO ── */}
      <section className="faq-hero">
        <div className="faq-hero-map-bg" style={{ backgroundImage: "url('/map.png')" }} />
        <div className="faq-hero-inner">
          <nav className="faq-breadcrumb">
            <Link to="/">Home</Link> / <span>FAQ</span>
          </nav>
          <h1 className="faq-hero-title">Frequently Asked Questions</h1>
          <p className="faq-hero-desc">
            Everything you need to know about container tracking, shipping codes, carriers, and RideLink Tracking features.
          </p>
        </div>
      </section>

      {/* ── FAQ CONTENT ── */}
      <section className="faq-content">
        <div className="faq-content-inner">
          {FAQ_CATEGORIES.map((cat, catIndex) => (
            <div key={catIndex} className="faq-category">
              <h2 className="faq-category-title">{cat.category}</h2>
              <div className="faq-list">
                {cat.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    className={`faq-item ${isOpen(catIndex, itemIndex) ? 'faq-item--open' : ''}`}
                  >
                    <button
                      className="faq-question"
                      onClick={() => toggle(catIndex, itemIndex)}
                      aria-expanded={isOpen(catIndex, itemIndex)}
                    >
                      <span>{item.question}</span>
                      <span className="faq-icon" aria-hidden="true">
                        {isOpen(catIndex, itemIndex) ? '−' : '+'}
                      </span>
                    </button>
                    <div className="faq-answer">
                      <p>{item.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="faq-cta">
        <div className="faq-cta-inner">
          <h2>Still have questions?</h2>
          <p>Browse our carrier pages for carrier-specific FAQs, or visit the container prefix guide to identify any shipping code.</p>
          <div className="faq-cta-links">
            <Link to="/carriers" className="faq-cta-btn faq-cta-btn--primary">View All Carriers</Link>
            <Link to="/container-prefixes" className="faq-cta-btn faq-cta-btn--secondary">Container Prefix List</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default FAQPage;
